import { useRef, useState } from "react";
import { FONT, COLOR, CHIP_TONES } from "../lib/tokens";
import { supabase, AUTH_REDIRECT_URL } from "../lib/supabase";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");
  // 6-digit code fallback. The auth emails carry BOTH a magic link
  // and a typed code ({{ .Token }} in the templates) because one-time
  // links die to email-provider link scanners (Yahoo prefetches the
  // link, consumes the token, and the human's tap lands on "expired")
  // and to spam-foldered mails found late. A typed code can't be
  // consumed by a bot.
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [codeError, setCodeError] = useState("");
  // Resend state — the "sent" screen needs its own resend affordance
  // (the error copy telling people to "send a fresh one" with no
  // button to do it was a dead end). idle | sending | sent
  const [resendPhase, setResendPhase] = useState("idle");
  // Password path (optional-everywhere): "magic" is the default;
  // "password" reveals a password field with sign-in / create-account
  // / forgot flows. Magic link + Google remain first-class.
  const [authMode, setAuthMode] = useState("magic"); // magic | password
  const [password, setPassword] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  // Rich error affordances for the password path. "badcreds" renders
  // three exits (retry / magic code / create account) because GoTrue
  // deliberately doesn't distinguish wrong-password from no-account.
  const [pwError, setPwError] = useState(null); // null | { kind: "badcreds"|"plain", message }
  const [signupMode, setSignupMode] = useState(false);
  // What the "sent" code screen is verifying: a magic/signup code
  // (verifyOtp type "email") or a password-recovery code (type
  // "recovery"). Recovery success ALSO stashes a flag so MainApp
  // opens the new-password sheet after this screen unmounts.
  const [sentContext, setSentContext] = useState("magic"); // magic | signup | recovery

  // MUST match the server's auth config `mailer_otp_length` (6) —
  // the boxes render one per digit and entry auto-verifies when the
  // last one fills. A mismatch here once truncated real codes into
  // guaranteed failures; if the server length ever changes, change
  // this constant with it.
  const CODE_LENGTH = 6;
  const codeInputRef = useRef(null);
  const [codeFocused, setCodeFocused] = useState(false);

  const doVerify = async (token) => {
    if (token.length !== CODE_LENGTH || verifying) return;
    setVerifying(true);
    setCodeError("");
    // type "email" covers both signup-confirmation and magic-link
    // codes in GoTrue's unified OTP verification; recovery codes use
    // their own type.
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token,
      type: sentContext === "recovery" ? "recovery" : "email",
    });
    setVerifying(false);
    if (error) {
      setCodeError(
        /expired|invalid/i.test(error.message || "")
          ? "That code didn't match or has expired — tap SEND A FRESH CODE below and use the newest email."
          : (error.message || "Couldn't verify the code."),
      );
      return;
    }
    // Recovery: the session lands and this screen unmounts before we
    // could render anything — stash a flag MainApp reads to open the
    // new-password sheet over the app. (The web link-tap path covers
    // itself via the PASSWORD_RECOVERY auth event; the typed-code
    // path needs this.)
    if (sentContext === "recovery") {
      try { localStorage.setItem("booda:recoveryPending", "1"); } catch { /* noop */ }
    }
    // Otherwise no handling — the auth listener in App.jsx picks up
    // the new session and swaps this screen out.
  };

  const verifyCode = (e) => {
    e?.preventDefault?.();
    doVerify(code.trim());
  };

  const onCodeChange = (e) => {
    const next = e.target.value.replace(/[^0-9]/g, "").slice(0, CODE_LENGTH);
    setCode(next);
    setCodeError("");
    // Auto-verify the moment the last box fills — the standard OTP
    // pattern; nobody should have to find a submit button after
    // typing the final digit.
    if (next.length === CODE_LENGTH) doVerify(next);
  };

  // ctxOverride: callers that setSentContext(...) and resend in the
  // same tick pass the context explicitly — the closure still sees
  // the pre-setState value, which once mailed a recovery email to a
  // user waiting on a signup confirmation code.
  const resend = async (ctxOverride) => {
    if (resendPhase === "sending" || !email.trim()) return;
    const ctx = typeof ctxOverride === "string" ? ctxOverride : sentContext;
    setResendPhase("sending");
    setCodeError("");
    setCode("");
    // Recovery codes come from the reset endpoint — resending a magic
    // code instead would verify against the wrong type.
    const { error } = ctx === "recovery"
      ? await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: AUTH_REDIRECT_URL })
      : await supabase.auth.signInWithOtp({
          email: email.trim(),
          options: { emailRedirectTo: AUTH_REDIRECT_URL },
        });
    if (error) {
      setResendPhase("idle");
      setCodeError(
        /rate/i.test(error.message || "")
          ? "Too many emails in a short window — wait a minute and try again."
          : (error.message || "Couldn't send a new email."),
      );
    } else {
      setResendPhase("sent");
      setTimeout(() => setResendPhase("idle"), 20000);
    }
  };

  // ── Password path ─────────────────────────────────────────────────

  const passwordSignIn = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || password.length === 0 || pwBusy) return;
    setPwBusy(true);
    setPwError(null);
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    setPwBusy(false);
    if (error) {
      if (/invalid login credentials/i.test(error.message || "")) {
        // GoTrue intentionally doesn't say whether the email exists.
        setPwError({ kind: "badcreds", message: "That email + password combo didn't work." });
      } else if (/email not confirmed/i.test(error.message || "")) {
        // Password account created but never confirmed — resend the
        // confirmation and route to the code screen.
        setPwError(null);
        setSentContext("signup");
        setPhase("sent");
        resend("signup");
      } else {
        setPwError({ kind: "plain", message: error.message || "Couldn't sign in." });
      }
      return;
    }
    // Success: App's auth listener swaps this screen out.
  };

  const createAccount = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || pwBusy) return;
    if (password.length < 8) {
      setPwError({ kind: "plain", message: "Passwords need at least 8 characters." });
      return;
    }
    setPwBusy(true);
    setPwError(null);
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { emailRedirectTo: AUTH_REDIRECT_URL },
    });
    setPwBusy(false);
    if (error) {
      setPwError({ kind: "plain", message: error.message || "Couldn't create the account." });
      return;
    }
    // Enumeration-safe "already registered" signal: GoTrue returns a
    // fake user with no identities instead of an error.
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      setPwError({
        kind: "plain",
        message: "This email already has an account. Sign in with a magic code, then set a password in Settings.",
      });
      return;
    }
    // Confirmation email (with link + 6-digit code) is on its way —
    // the session lands with the password already attached once the
    // code verifies. Reuse the sent screen unchanged.
    setSentContext("signup");
    setPhase("sent");
  };

  const forgotPassword = async () => {
    if (!email.trim() || pwBusy) return;
    setPwBusy(true);
    setPwError(null);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: AUTH_REDIRECT_URL,
    });
    setPwBusy(false);
    if (error) {
      setPwError({ kind: "plain", message: error.message || "Couldn't send the reset email." });
      return;
    }
    setSentContext("recovery");
    setPhase("sent");
  };

  const send = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    // Reset the sent-screen context: a prior FORGOT PASSWORD or
    // signup attempt in this mount leaves "recovery"/"signup" behind,
    // which would make doVerify use the wrong OTP type and show the
    // wrong copy for this plain magic code.
    setSentContext("magic");
    setPhase("sending");
    setErrorMsg("");
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: AUTH_REDIRECT_URL },
    });
    if (error) {
      setErrorMsg(error.message);
      setPhase("error");
    } else {
      setPhase("sent");
    }
  };

  const signInWithGoogle = async () => {
    setErrorMsg("");

    // OAuth strategy on native iOS:
    //   1. Get the Google auth URL via skipBrowserRedirect so we
    //      control the navigation.
    //   2. Open it in EXTERNAL Safari (not SFSafariViewController).
    //      External Safari handles custom URL schemes like
    //      app.booda.ios:// reliably; SFSafariViewController
    //      silently blocks them on most iOS versions.
    //   3. The host WebView stays put on the sign-in screen, so
    //      localStorage / sessionStorage / the PKCE code verifier
    //      all survive the round-trip.
    //   4. When the user finishes auth, Supabase redirects to the
    //      custom scheme, iOS routes the app to the foreground,
    //      and the appUrlOpen listener in App.jsx finishes the
    //      exchange.
    // On web, Supabase navigates window.location like normal.
    const isNative = Capacitor.isNativePlatform();

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: AUTH_REDIRECT_URL,
        skipBrowserRedirect: isNative,
        // Force Google to show the account picker every time instead
        // of silently re-auth'ing the most recent account. Critical
        // for users who run multiple Google identities (testing
        // family-share flows with two accounts on the same device,
        // sign-out → swap-account, etc.). Without `prompt`, Google
        // SSO returns the cached session and the app loops back into
        // the same identity.
        queryParams: { prompt: "select_account" },
      },
    });
    if (error) {
      setErrorMsg(error.message);
      setPhase("error");
      return;
    }

    if (isNative && data?.url) {
      // Open in EXTERNAL Safari (not SFSafariViewController) so the
      // custom URL scheme redirect actually fires the app's
      // deep-link handler. Capacitor's Browser plugin defaults to
      // SFSafariViewController; passing windowName "_self" with
      // window.open('_system') gives us external Safari.
      try {
        // Capacitor intercepts window.open with target=_system on
        // iOS and opens external Safari via UIApplication.openURL.
        const opened = window.open(data.url, "_system");
        // Some Capacitor versions return null; fallback to Browser
        // plugin if window.open didn't take.
        if (!opened) {
          await Browser.open({ url: data.url });
        }
      } catch (e) {
        setErrorMsg(e?.message || "Couldn't open sign-in window.");
        setPhase("error");
      }
    }
  };

  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", minHeight: "100vh", padding: 32, textAlign: "center",
    }}>
      <img
        src="/boodalogo.png"
        alt="Booda"
        style={{ width: 240, height: "auto", marginBottom: 24 }}
      />
      <p style={{
        fontFamily: FONT.sans, fontSize: 14, color: "rgba(250, 246, 238, 0.72)",
        maxWidth: 280, lineHeight: 1.5, marginBottom: 36,
      }}>
        {phase === "sent"
          ? (sentContext === "recovery"
              ? "Check your email (spam folder too). Type the 6-digit reset code below, then choose a new password."
              : sentContext === "signup"
                ? "Almost there — confirm your email. Tap the link, or type the code from the email below."
                : "Check your email (spam folder too). Tap the link, or type the code from the email below.")
          : authMode === "password"
            ? (signupMode
                ? "Pick a password (8+ characters) and we'll email you a confirmation code."
                : "Sign in with your email and password.")
            : "Sign in with your email. We'll send you a magic link — no passwords."}
      </p>

      {phase !== "sent" && (
        <div style={{ width: "100%", maxWidth: 320, display: "flex", flexDirection: "column", gap: 14 }}>
          <button
            type="button"
            onClick={signInWithGoogle}
            style={{
              padding: "14px", borderRadius: 12,
              border: `1px solid ${COLOR.border}`, background: COLOR.ink,
              color: COLOR.night, fontFamily: FONT.sans,
              fontSize: 14, fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              transition: "all 0.2s",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 7.1 29.3 5 24 5 16.2 5 9.4 9.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.3-7.2 2.3-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.2 39.6 16 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2C41.8 35.4 44 30 44 24c0-1.2-.1-2.3-.4-3.5z"/>
            </svg>
            Continue with Google
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, height: 1, background: COLOR.edge }} />
            <span style={{ fontFamily: FONT.mono, fontSize: 12, color: COLOR.sub, letterSpacing: "0.15em" }}>OR</span>
            <div style={{ flex: 1, height: 1, background: COLOR.edge }} />
          </div>
        </div>
      )}

      {phase !== "sent" && (
        <form
          onSubmit={authMode === "password" ? (signupMode ? createAccount : passwordSignIn) : send}
          style={{ width: "100%", maxWidth: 320, display: "flex", flexDirection: "column", gap: 12, marginTop: 14 }}
        >
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            autoFocus
            disabled={phase === "sending"}
            style={{
              padding: "14px 16px", borderRadius: 12,
              border: `1px solid ${COLOR.border}`, background: COLOR.soil,
              color: COLOR.ink, fontFamily: FONT.sans, fontSize: 15,
              outline: "none", textAlign: "center",
            }}
          />
          {authMode === "password" && (
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setPwError(null); }}
              placeholder={signupMode ? "Choose a password (8+)" : "Password"}
              autoComplete={signupMode ? "new-password" : "current-password"}
              disabled={pwBusy}
              style={{
                padding: "14px 16px", borderRadius: 12,
                border: `1px solid ${COLOR.border}`, background: COLOR.soil,
                color: COLOR.ink, fontFamily: FONT.sans, fontSize: 15,
                outline: "none", textAlign: "center",
              }}
            />
          )}
          <button
            type="submit"
            disabled={!email.trim() || phase === "sending" || pwBusy
              || (authMode === "password" && password.length === 0)}
            style={{
              padding: "14px", borderRadius: 12, border: "none",
              background: email.trim() ? COLOR.gold : COLOR.soil,
              color: email.trim() ? COLOR.night : COLOR.sub,
              fontFamily: FONT.mono, fontSize: 13, fontWeight: 600,
              letterSpacing: "0.08em",
              cursor: email.trim() && phase !== "sending" && !pwBusy ? "pointer" : "not-allowed",
              transition: "all 0.2s",
            }}
          >
            {authMode === "password"
              ? (pwBusy ? "WORKING…" : signupMode ? "CREATE ACCOUNT →" : "SIGN IN →")
              : (phase === "sending" ? "SENDING…" : "SEND MAGIC LINK →")}
          </button>
          {phase === "error" && authMode === "magic" && (
            <div style={{
              padding: "10px 12px", background: COLOR.roseDeep,
              border: `1px solid ${COLOR.roseDim}`, borderRadius: 10,
              fontFamily: FONT.sans, fontSize: 13, color: COLOR.rose,
            }}>
              {errorMsg || "Something went wrong. Try again."}
            </div>
          )}
          {pwError && authMode === "password" && (
            <div style={{
              padding: "12px", background: COLOR.roseDeep,
              border: `1px solid ${COLOR.roseDim}`, borderRadius: 10,
              fontFamily: FONT.sans, fontSize: 13, color: COLOR.rose,
              display: "flex", flexDirection: "column", gap: 8, textAlign: "left",
            }}>
              <span>{pwError.message}</span>
              {pwError.kind === "badcreds" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <button type="button" onClick={() => { setPwError(null); setPassword(""); }}
                    style={{ background: "none", border: "none", padding: "6px 0", textAlign: "left", fontFamily: FONT.mono, fontSize: 12, fontWeight: 600, color: COLOR.ink, letterSpacing: "0.06em", cursor: "pointer" }}>
                    TRY AGAIN
                  </button>
                  <button type="button" onClick={(e) => { setPwError(null); setAuthMode("magic"); send(e); }}
                    style={{ background: "none", border: "none", padding: "6px 0", textAlign: "left", fontFamily: FONT.mono, fontSize: 12, fontWeight: 600, color: COLOR.ink, letterSpacing: "0.06em", cursor: "pointer" }}>
                    EMAIL ME A MAGIC CODE INSTEAD
                  </button>
                  <button type="button" onClick={() => { setPwError(null); setSignupMode(true); }}
                    style={{ background: "none", border: "none", padding: "6px 0", textAlign: "left", fontFamily: FONT.mono, fontSize: 12, fontWeight: 600, color: COLOR.ink, letterSpacing: "0.06em", cursor: "pointer" }}>
                    NEW HERE? CREATE AN ACCOUNT WITH THIS PASSWORD
                  </button>
                </div>
              )}
            </div>
          )}
          {authMode === "magic" ? (
            <button
              type="button"
              onClick={() => { setAuthMode("password"); setPhase("idle"); setErrorMsg(""); }}
              style={{
                background: "none", border: "none", padding: "8px 4px",
                fontFamily: FONT.mono, fontSize: 12, color: COLOR.muted,
                letterSpacing: "0.08em", cursor: "pointer",
              }}
            >
              USE A PASSWORD INSTEAD
            </button>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {!signupMode && (
                <button
                  type="button"
                  onClick={forgotPassword}
                  disabled={!email.trim() || pwBusy}
                  style={{
                    background: "none", border: "none", padding: "8px 4px",
                    fontFamily: FONT.mono, fontSize: 12,
                    color: email.trim() ? COLOR.muted : COLOR.sub,
                    letterSpacing: "0.08em", cursor: email.trim() ? "pointer" : "not-allowed",
                  }}
                >
                  FORGOT PASSWORD?
                </button>
              )}
              <button
                type="button"
                onClick={() => { setSignupMode(!signupMode); setPwError(null); }}
                style={{
                  background: "none", border: "none", padding: "8px 4px",
                  fontFamily: FONT.mono, fontSize: 12, color: COLOR.muted,
                  letterSpacing: "0.08em", cursor: "pointer",
                }}
              >
                {signupMode ? "ALREADY HAVE AN ACCOUNT? SIGN IN" : "NEW HERE? CREATE AN ACCOUNT"}
              </button>
              <button
                type="button"
                onClick={() => { setAuthMode("magic"); setSignupMode(false); setPwError(null); setPassword(""); }}
                style={{
                  background: "none", border: "none", padding: "8px 4px",
                  fontFamily: FONT.mono, fontSize: 12, color: COLOR.muted,
                  letterSpacing: "0.08em", cursor: "pointer",
                }}
              >
                USE A MAGIC CODE INSTEAD
              </button>
            </div>
          )}
        </form>
      )}

      {phase === "sent" && (
        <form onSubmit={verifyCode} style={{ width: "100%", maxWidth: 320, display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Segmented OTP entry — one box per digit, standard
              platform pattern. A single INVISIBLE input overlays the
              boxes: it owns focus, the keyboard, and iOS's
              one-time-code autofill (which needs a real input);
              the boxes are pure rendering of its value. fontSize 16
              on the hidden input is load-bearing — anything smaller
              makes iOS zoom the viewport on focus. */}
          <div
            onClick={() => codeInputRef.current?.focus()}
            style={{ position: "relative", display: "flex", gap: 6, justifyContent: "center" }}
          >
            {Array.from({ length: CODE_LENGTH }).map((_, i) => {
              const digit = code[i] || "";
              const isActive = codeFocused && !verifying
                && i === Math.min(code.length, CODE_LENGTH - 1)
                && code.length < CODE_LENGTH;
              return (
                <div
                  key={i}
                  style={{
                    width: 42, height: 50, borderRadius: 10,
                    border: `1.5px solid ${isActive ? COLOR.gold : digit ? COLOR.goldDim : COLOR.border}`,
                    background: COLOR.soil,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: FONT.mono, fontSize: 20, fontWeight: 600,
                    color: COLOR.ink,
                    transition: "border-color 0.15s",
                  }}
                >
                  {digit}
                </div>
              );
            })}
            <input
              ref={codeInputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              value={code}
              onChange={onCodeChange}
              onFocus={() => setCodeFocused(true)}
              onBlur={() => setCodeFocused(false)}
              disabled={verifying}
              autoFocus
              aria-label="Sign-in code from the email"
              style={{
                position: "absolute", inset: 0, width: "100%", height: "100%",
                opacity: 0.02, border: "none", outline: "none",
                background: "transparent", color: "transparent",
                caretColor: "transparent", fontSize: 16,
                textAlign: "center",
              }}
            />
          </div>
          <button
            type="submit"
            disabled={code.length !== CODE_LENGTH || verifying}
            style={{
              padding: "14px", borderRadius: 12, border: "none",
              background: code.length === CODE_LENGTH ? COLOR.gold : COLOR.soil,
              color: code.length === CODE_LENGTH ? COLOR.night : COLOR.sub,
              fontFamily: FONT.mono, fontSize: 13, fontWeight: 600,
              letterSpacing: "0.08em",
              cursor: code.length === CODE_LENGTH && !verifying ? "pointer" : "not-allowed",
              transition: "all 0.2s",
            }}
          >
            {verifying ? "CHECKING…" : "SIGN IN →"}
          </button>
          {codeError && (
            <div style={{
              padding: "10px 12px", background: COLOR.roseDeep,
              border: `1px solid ${COLOR.roseDim}`, borderRadius: 10,
              fontFamily: FONT.sans, fontSize: 13, color: COLOR.rose,
            }}>
              {codeError}
            </div>
          )}
          <button
            type="button"
            onClick={resend}
            disabled={resendPhase === "sending"}
            style={{
              padding: "12px", borderRadius: 12,
              border: `1px solid ${COLOR.border}`, background: "transparent",
              color: resendPhase === "sent" ? COLOR.leaf : COLOR.dim,
              fontFamily: FONT.mono, fontSize: 12, fontWeight: 600,
              letterSpacing: "0.08em",
              cursor: resendPhase === "sending" ? "progress" : "pointer",
            }}
          >
            {resendPhase === "sending" ? "SENDING…"
              : resendPhase === "sent" ? "SENT — CHECK YOUR EMAIL"
              : "SEND A FRESH CODE"}
          </button>
          <button
            type="button"
            onClick={() => { setPhase("idle"); setEmail(""); setCode(""); setCodeError(""); setResendPhase("idle"); }}
            style={{
              marginTop: 6, background: "none", border: "none",
              color: COLOR.muted, fontFamily: FONT.mono, fontSize: 12,
              letterSpacing: "0.08em", cursor: "pointer", padding: "8px 4px",
            }}
          >
            USE A DIFFERENT EMAIL
          </button>
        </form>
      )}
    </div>
  );
}
